import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { CentralMappingCategory } from 'lumen/models';
import {
  type Breakdown,
  BREAKDOWNS,
} from 'lumen/utils/central-mapping-constants';

import styles from './modal-confirmation-delete-category.module.css';

interface SettingsCentralMappingModalsModalConfirmationDeleteCategorySignature {
  Args: {
    breakdown: Breakdown;
    category: CentralMappingCategory;
    onClose: () => void;
    onDiscard: () => void;
  };
}

export default class SettingsCentralMappingModalsModalConfirmationDeleteCategory extends Component<SettingsCentralMappingModalsModalConfirmationDeleteCategorySignature> {
  @service declare intl: Services['intl'];

  @tracked inputValue = '';

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get categoryHasChildren() {
    return this.args.category.hasChildren;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get confirmationText() {
    return this.intl.t('general.delete');
  }

  get descriptionTranslation(): string {
    if (this.categoryHasChildren) {
      if (this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT) {
        return this.intl.t(
          'central_mapping.confirmation_modal.delete_category.description.has_subcategories.market_segmentation',
        );
      }

      return this.intl.t(
        'central_mapping.confirmation_modal.delete_category.description.has_subcategories',
      );
    }

    if (this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT) {
      return this.intl.t(
        'central_mapping.confirmation_modal.delete_category.description.market_segmentation',
      );
    }

    return this.intl.t(
      'central_mapping.confirmation_modal.delete_category.description',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDeleteButtonDisabled() {
    return (
      this.inputValue.toLowerCase() !== this.confirmationText.toLowerCase()
    );
  }

  @action onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.isDeleteButtonDisabled) {
      this.args.onDiscard();
    }
  }

  @action setInputValue(event: Event): void {
    this.inputValue = (event.target as HTMLInputElement).value;
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      class={{styles.confirmation-modal}}
      data-test-id="central-mapping-confirmation-modal-delete-category"
    >
      <:default>
        <div class="flex flex-col items-center justify-center">
          <PrismMessage
            @description={{this.descriptionTranslation}}
            @illustration="100x100_warning"
            @title={{if
              this.categoryHasChildren
              (t
                "central_mapping.confirmation_modal.delete_category.title.has_subcategories"
              )
              (t "central_mapping.confirmation_modal.delete_category.title")
            }}
          />

          <PrismInput
            @autocomplete="off"
            @autofocus={{true}}
            @label={{t
              "central_mapping.confirmation_modal.type_to_confirm"
              text=this.confirmationText
            }}
            @name="confirm"
            @onInput={{this.setInputValue}}
            @onKeyDown={{this.onKeyDown}}
            @type="text"
            @value={{this.inputValue}}
            data-test-id="central-mapping-confirmation-modal-input"
          />
        </div>
      </:default>

      <:footer>
        <div class="m-300 flex justify-end gap-300">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-id="central-mapping-confirmation-modal-cancel-button"
          />

          <PrismButton
            @buttonType="critical"
            @isDisabled={{this.isDeleteButtonDisabled}}
            @label={{t
              "central_mapping.confirmation_modal.actions.delete_and_unmap"
            }}
            @onClick={{@onDiscard}}
            data-test-id="central-mapping-confirmation-modal-delete-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
